import GoogleIcon from "@mui/icons-material/Google";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import {
  Link as RouterLink,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { z } from "zod";
import { getAuthErrorMessage } from "@bw/shared/auth-errors";
import { useAuth } from "@bw/shared/hooks";
import { sanitizeNext } from "@bw/shared/sanitize-next";

const signupFormSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Invalid email address"),
    password: z.string().min(6, "Password must be at least 6 characters"),
    confirmPassword: z.string().min(6, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  });

type SignupFormValues = z.infer<typeof signupFormSchema>;

export function SignupForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const next = sanitizeNext(searchParams.get("next")) ?? "/workspaces";
  const { authError, signIn, signUpWithEmail } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const form = useForm<SignupFormValues>({
    resolver: zodResolver(signupFormSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      confirmPassword: "",
    },
  });

  const run = async (action: () => Promise<unknown> | undefined) => {
    setError(null);
    setSubmitting(true);
    try {
      await action();
      navigate(next, { replace: true });
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (data: SignupFormValues) =>
    run(() =>
      signUpWithEmail?.(
        data.email,
        data.password,
        `${data.firstName} ${data.lastName}`.trim(),
        next,
      ),
    );

  const beginGoogleSignIn = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await signIn();
      // Google redirects away from this document. When the browser returns,
      // SignUpPage's useAuthedRedirect preserves the safe destination.
    } catch (err) {
      setError(getAuthErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      <Paper
        component="form"
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        variant="outlined"
        sx={{ p: { xs: 2.5, sm: 3 } }}
      >
        <Stack spacing={2.25}>
          <Typography color="text.secondary" variant="body2">
            One account to play, coach, or join your organization.
          </Typography>
          {(error ?? authError) ? (
            <Alert severity="error" role="alert">
              {error ?? authError}
            </Alert>
          ) : null}
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            }}
          >
            <TextField
              autoComplete="given-name"
              error={Boolean(form.formState.errors.firstName)}
              helperText={form.formState.errors.firstName?.message}
              label="First name"
              {...form.register("firstName")}
            />
            <TextField
              autoComplete="family-name"
              error={Boolean(form.formState.errors.lastName)}
              helperText={form.formState.errors.lastName?.message}
              label="Last name"
              {...form.register("lastName")}
            />
          </Box>
          <TextField
            autoComplete="email"
            error={Boolean(form.formState.errors.email)}
            helperText={form.formState.errors.email?.message}
            label="Email"
            placeholder="you@example.com"
            type="email"
            {...form.register("email")}
          />
          <TextField
            autoComplete="new-password"
            error={Boolean(form.formState.errors.password)}
            helperText={form.formState.errors.password?.message}
            label="Password"
            type="password"
            {...form.register("password")}
          />
          <TextField
            autoComplete="new-password"
            error={Boolean(form.formState.errors.confirmPassword)}
            helperText={form.formState.errors.confirmPassword?.message}
            label="Confirm password"
            type="password"
            {...form.register("confirmPassword")}
          />
          <Button
            disabled={submitting}
            fullWidth
            startIcon={
              submitting ? (
                <CircularProgress
                  aria-hidden="true"
                  color="inherit"
                  size={16}
                />
              ) : undefined
            }
            type="submit"
            variant="contained"
          >
            {submitting ? "Creating account…" : "Create account"}
          </Button>
          <Divider>or</Divider>
          <Button
            disabled={submitting}
            fullWidth
            onClick={() => void beginGoogleSignIn()}
            startIcon={<GoogleIcon />}
            type="button"
            variant="outlined"
          >
            Sign up with Google
          </Button>
          <Typography variant="body2" sx={{ textAlign: "center" }}>
            Already have an account?{" "}
            <Link
              component={RouterLink}
              to={`/sign-in?next=${encodeURIComponent(next)}`}
            >
              Sign in
            </Link>
          </Typography>
        </Stack>
      </Paper>
      <Typography
        color="text.secondary"
        variant="caption"
        sx={{ textAlign: "center" }}
      >
        Review our nonbinding prelaunch{" "}
        <Link component={RouterLink} to="/terms">
          Terms of Service draft
        </Link>{" "}
        and{" "}
        <Link component={RouterLink} to="/privacy">
          Privacy Policy draft
        </Link>
        .
      </Typography>
    </Stack>
  );
}
